import isEmpty from 'lodash/isEmpty';
import { type ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { ForgeIcon } from '@tylertech/forge-react';
import { I18n } from 'common/i18n';
import { type PicklistOption } from 'common/components/Picklist';
import Dropdown from 'common/components/Dropdown';
import { EnvironmentFactory } from 'StorytellerEnvironment';
import { ComponentType, type BlockComponent } from 'types';
import { isFlexibleStory } from 'lib/FlexibleLayoutUtils';
import { COMPONENT_ACTION_TYPES } from 'lib/Constants';
import { isComponentTypeMovable } from 'lib/StoryHelper';
import { StorytellerReduxStoreFactory } from 'store/StorytellerReduxStore';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import { actionComponentStart } from 'store/reducers/ActionComponentReducer';
import {
  copyComponent,
  insertStoryTableOfContents,
  pasteComponent,
  resetComponent,
} from 'store/reducers/StoryReducer';
import { editExistingAssetEmbed } from 'store/reducers/AssetSelectorReducer';

/**
 * Renders a block component menu that dispatches several component-level
 * actions, such as editing the component, and moving/swapping components.
 *
 * This can be located in the UI by dropping a media block in the story, configuring
 * it, and then mousing over the result. Buttons anchored on the top-right of the
 * component container will appear.
 */

export const ComponentEditMenu = ({
  blockId,
  componentData,
  componentIndex,
  editMode,
}: {
  blockId: string;
  componentIndex: number;
  componentData: BlockComponent;
  editMode: boolean;
}) => {
  const dispatch = useDispatch<any>();

  if (!editMode) {
    return null;
  }

  const options = (): PicklistOption[] => {
    const o = [
      {
        title: I18n.t('editor.components.edit_controls.move'),
        group: I18n.t('editor.components.edit_controls.groups.actions'),
        icon: 'cursor_move',
        value: 'move',
        render: renderKebabPicklistOption,
      },
    ];

    if (isNotAssetSelector()) {
      o.push({
        title: I18n.t('editor.components.edit_controls.copy'),
        group: I18n.t('editor.components.edit_controls.groups.actions'),
        icon: 'content_copy',
        value: 'copy',
        render: renderKebabPicklistOption,
      });
    }

    if (shouldUsePasteButton()) {
      o.push({
        title: I18n.t('editor.components.edit_controls.paste'),
        group: I18n.t('editor.components.edit_controls.groups.actions'),
        icon: 'content_paste',
        value: 'paste',
        render: renderKebabPicklistOption,
      });
    }

    if (isNotAssetSelector()) {
      o.push({
        title: I18n.t('editor.components.edit_controls.reset'),
        group: I18n.t('editor.components.edit_controls.groups.actions'),
        icon: 'close',
        value: 'reset',
        render: renderKebabPicklistOption,
      });
    }

    return o;
  };

  const shouldUsePasteButton = (): boolean => {
    try {
      return !storySelectors.isBlockRestrictedToMeasureCardsOnly(
        StorytellerReduxStoreFactory().getState(),
        blockId,
      );
    } catch (e) {
      // This mainly happens in tests, where we render components without the whole story.
      return true;
    }
  };

  const isNotAssetSelector = (): boolean => {
    return (
      componentData.type !== ComponentType.ASSET_SELECTOR && componentData.type !== ComponentType.CLASSIC_VIZ
    );
  };

  const clickEditButton = (): void => {
    dispatch(
      editExistingAssetEmbed({
        blockId,
        componentIndex,
        componentType: componentData.type,
        componentValue: componentData.value,
      }),
    );
  };

  const clickUpdateTableButton = (): void => {
    const storyUid = EnvironmentFactory().STORY_UID;
    dispatch(insertStoryTableOfContents({ storyUid, blockWithToCId: blockId }));
  };

  const selectKebabPicklistItem = (option: PicklistOption): void => {
    switch (option.value) {
      case 'reset':
        dispatch(resetComponent({ blockId, componentIndex }));
        break;
      case 'move':
        dispatch(
          actionComponentStart({
            blockId,
            componentIndex,
            actionType: COMPONENT_ACTION_TYPES.MOVE,
          }),
        );
        break;
      case 'copy':
        dispatch(copyComponent({ blockId, componentIndex }));
        break;
      case 'paste':
        // @ts-expect-error TS(2769) FIXME: No overload matches this call.
        dispatch(pasteComponent({ blockId, componentIndex }));
        break;
      default:
        break;
    }
  };

  const renderKebabPicklistOption = (option: PicklistOption): ReactElement => {
    return (
      <div>
        <ForgeIcon name={option.icon} />
        <span>{option.title}</span>
      </div>
    );
  };

  const renderKebabPicklist = (): ReactElement => {
    const attributes = {
      onSelection: selectKebabPicklistItem,
      options: options(),
      placeholder: renderKebabButton,
      skipPositionPicklist: true,
    };

    return <Dropdown {...attributes} />;
  };

  const renderKebabButton = (): ReactElement => {
    const attributes = {
      className: 'component-edit-controls-btn component-edit-controls-kebab-btn',
    };
    return (
      <button
        {...attributes}
        data-testid="component-edit-controls-kebab-btn"
        aria-label={I18n.t('editor.components.edit_controls.groups.actions')}
      >
        <span className="icon-kebab" />
      </button>
    );
  };

  const renderEditButton = (): ReactElement => {
    return (
      <button
        className="component-edit-controls-btn component-edit-controls-edit-btn"
        onClick={clickEditButton}
        aria-label={I18n.t('editor.components.edit_controls.button')}
      >
        {I18n.t('editor.components.edit_controls.button')}
      </button>
    );
  };

  const renderUpdateTableButton = (): ReactElement => {
    return (
      <button
        className="component-edit-controls-btn component-edit-controls-update-table-btn"
        onClick={clickUpdateTableButton}
      >
        {I18n.t('editor.components.table_of_contents.update_table')}
      </button>
    );
  };

  const isEditButtonSupported = () => {
    if (componentData.type === ComponentType.HERO) {
      return editMode && !isEmpty(componentData.value) && !isEmpty(componentData.value.url);
    }

    if (componentData.type === ComponentType.AUTHOR) {
      return editMode && !isEmpty(componentData.value) && !isEmpty(componentData.value?.image?.url);
    }

    return (
      editMode &&
      ![
        ComponentType.ASSET_SELECTOR,
        ComponentType.CLASSIC_VIZ,
        ComponentType.HORIZONTAL_RULE,
        ComponentType.HTML,
        ComponentType.HTML_TABLE_OF_CONTENTS,
        ComponentType.GLOBAL_FILTER,
      ].includes(componentData.type)
    );
  };

  const updateTableButtonSupported = componentData.type === ComponentType.HTML_TABLE_OF_CONTENTS;

  // NOTE: The same criteria apply for a component's ability to be reset, so
  // the kebabEnabled check is doing double duty.

  const kebabEnabled = isComponentTypeMovable(componentData.type);
  const kebabButton = kebabEnabled ? renderKebabPicklist() : null;
  const editButton = isEditButtonSupported() ? renderEditButton() : null;
  const updateTableButton = updateTableButtonSupported ? renderUpdateTableButton() : null;

  if (!editButton && !updateTableButton && !kebabButton) {
    return null;
  }

  const classes = classNames('component-edit-controls-container component-base', {
    'with-dragger': isFlexibleStory() && componentData.type !== ComponentType.GLOBAL_FILTER,
    'has-update-table-btn': updateTableButtonSupported,
  });

  return (
    <div className={classes}>
      <div className="component-edit-controls" data-testid="component-edit-controls">
        {editButton}
        {updateTableButton}
        {kebabButton}
      </div>
    </div>
  );
};
